import { FileOutlined } from '@ant-design/icons';
import { Button, Menu, MenuProps, Upload } from 'antd';
import React, { FC } from 'react';
import { compose } from 'redux';
import { SubPermissions, User, getPermissionErrorMessage } from '../../../../model/AccountModel';
import { GraphicPageMode } from '../../../../model/GraphicModel';
import { WithNewGraphicModal } from '../../../common';
import { MenuItems, withMenuActions } from '../../../graphic';
import {
	AddParagraph,
	AddText,
	AlarmQueue,
	BirdEye,
	BringForward,
	BringToFront,
	Copy,
	Cut,
	Delete,
	DeleteRedline,
	Devices,
	Drawing,
	ExportFile,
	File,
	FlipHorizontal,
	FlipVertical,
	Group,
	ImportFile,
	LineColor,
	MapDirectory,
	MonitorZone,
	Move,
	ObjectProperties,
	PageSetup,
	PaintBucket,
	Paste,
	Pin,
	PlottedDevices,
	QuickAccess,
	RedlineArrow,
	RedlineCircle,
	RedlineFilledRectangle,
	RedlineFreeHand,
	RedlineRectangle,
	RedoInactive,
	Resize,
	Rotate,
	Save,
	SearchText,
	Select,
	Selection,
	SendBackward,
	SendToBack,
	Text,
	UndoInactive,
	Ungroup,
	View,
} from '../Icons';
import styles from '../designMenuBar.module.scss';

import { CanvasInfo } from '../../../../model/BadgeModel';
import { LineWeight } from '../../../../resources/Icons';
const user: User = getUser();
const canUseAlarmViewer: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmViewer_Use).allowed;

const withMyHOCs = compose(WithNewGraphicModal, withMenuActions);

const MainMenu: FC<CanvasInfo> = withMyHOCs(({ disabled, uploadProps, handleMenuActions, handleOnOpenNewModal, isMaps, viewMode, redlineConfiguration }) => {
	const isDesignMode = viewMode === GraphicPageMode.Design;
	const viewStyle = !isDesignMode || !isMaps ? { display: 'none' } : undefined;
	const style = !isDesignMode ? { display: 'none' } : undefined;

	const items: MenuProps['items'] = [
		{
			disabled,
			icon: <File elementStyle={{ height: 20 }} />,
			key: MenuItems.File,
			popupClassName: 'graphicsMenuBarSubMenu graphicsMenuBarSubMenuFile',
			popupOffset: [0, 1],
			title: _('File'),
			label: _('File'),
			children: [
				{
					key: MenuItems.NewFile,
					disabled: !isDesignMode,
					icon: <FileOutlined />,
					onClick: () => {
						handleMenuActions(MenuItems.NewFile).then(() => {
							handleOnOpenNewModal();
						});
					},
					title: _('New'),
				},
				{
					key: MenuItems.Save,
					disabled: !isDesignMode,
					icon: <Save />,
					onClick: () => handleMenuActions(MenuItems.Save),
					title: _('Save'),
				},
				{
					key: MenuItems.ImportFile,
					disabled: !isDesignMode,
					onClick: () => handleMenuActions(MenuItems.ImportFile),
					title: _('ImportFile'),
					label: (
						<Upload {...uploadProps}>
							<Button type='link' icon={<ImportFile />} />
						</Upload>
					),
				},
				{
					key: MenuItems.Directory,
					icon: <MapDirectory />,
					onClick: () => handleMenuActions(MenuItems.Directory),
					title: _('Directory'),
				},
				{
					key: MenuItems.ExportFile,
					disabled: !isDesignMode,
					className: styles.divider,
					icon: <ExportFile />,
					onClick: () => handleMenuActions(MenuItems.ExportFile),
					title: _('ExportFile'),
				},
				{
					key: MenuItems.Undo,
					disabled: !isDesignMode,
					icon: <UndoInactive />,
					onClick: () => handleMenuActions(MenuItems.Undo),
					title: _('Undo'),
				},
				{
					key: MenuItems.Redo,
					disabled: !isDesignMode,
					icon: <RedoInactive />,
					onClick: () => handleMenuActions(MenuItems.Redo),
					title: _('Redo'),
				},
				{
					key: MenuItems.Copy,
					disabled: !isDesignMode,
					icon: <Copy />,
					onClick: () => handleMenuActions(MenuItems.Copy),
					title: _('Copy'),
				},
				{
					key: MenuItems.Paste,
					disabled: !isDesignMode,
					icon: <Paste />,
					onClick: () => handleMenuActions(MenuItems.Paste),
					title: _('Paste'),
				},
				{
					key: MenuItems.Cut,
					disabled: !isDesignMode,
					icon: <Cut />,
					onClick: () => handleMenuActions(MenuItems.Cut),
					title: _('Cut'),
				},
				{
					key: MenuItems.PageSetup,
					disabled: !isDesignMode,
					icon: <PageSetup />,
					onClick: () => handleMenuActions(MenuItems.PageSetup),
					title: _('PageSetup'),
				},
			],
		},
		{
			style: viewStyle,
			disabled,
			icon: <View elementStyle={{ height: 20, width: 20 }} />,
			key: MenuItems.View,
			popupClassName: 'graphicsMenuBarSubMenu graphicsMenuBarSubMenuView',
			popupOffset: [0, 1],
			title: _('View'),
			label: _('View'),
			children: [
				{
					key: MenuItems.ViewDevices,
					icon: <Devices elementStyle={{ height: 20 }} />,
					onClick: () => handleMenuActions(MenuItems.ViewDevices),
					title: _('Devices'),
				},
				{
					key: MenuItems.ViewAlarmQueue,
					icon: <AlarmQueue elementStyle={{ height: 20 }} />,
					onClick: () => handleMenuActions(MenuItems.ViewAlarmQueue),
					disabled: !canUseAlarmViewer,
					title: getPermissionErrorMessage(canUseAlarmViewer) || _('AlarmQueue'),
				},
				{
					key: MenuItems.ViewPlottedDevices,
					icon: <PlottedDevices elementStyle={{ height: 20 }} />,
					onClick: () => handleMenuActions(MenuItems.ViewPlottedDevices),
					title: _('PlottedDevices'),
				},
				{
					key: MenuItems.ViewObjectProperties,
					icon: <ObjectProperties elementStyle={{ height: 20 }} />,
					onClick: () => handleMenuActions(MenuItems.ViewObjectProperties),
					title: _('ObjectProperties'),
				},
				{
					key: MenuItems.ViewQuickAccess,
					icon: <QuickAccess elementStyle={{ height: 20 }} />,
					onClick: () => handleMenuActions(MenuItems.ViewQuickAccess),
					title: _('QuickAccess'),
				},
				{
					key: MenuItems.ViewBirdEye,
					icon: <BirdEye elementStyle={{ height: 20 }} />,
					onClick: () => handleMenuActions(MenuItems.ViewBirdEye),
					title: _('BirdEye'),
				},
			],
		},
		{
			style,
			disabled,
			icon: <Drawing elementStyle={{ height: 20 }} />,
			key: MenuItems.Drawing,
			popupClassName: 'graphicsMenuBarSubMenu graphicsMenuBarSubMenuDrawing',
			popupOffset: [0, 1],
			title: _('Drawing'),
			label: _('Drawing'),
			children: [
				{
					key: MenuItems.RedlineFreeHand,
					icon: <RedlineFreeHand />,
					onClick: () => handleMenuActions(MenuItems.RedlineFreeHand),
					title: _('Line'),
				},
				{
					key: MenuItems.RedlineRectangle,
					icon: <RedlineRectangle />,
					onClick: () => handleMenuActions(MenuItems.RedlineRectangle),
					title: _('Rectangle'),
				},
				{
					key: MenuItems.RedlineFilledRectangle,
					icon: <RedlineFilledRectangle />,
					onClick: () => handleMenuActions(MenuItems.RedlineFilledRectangle),
					title: _('RectangleFill'),
				},
				{
					key: MenuItems.RedlineCircle,
					icon: <RedlineCircle />,
					onClick: () => handleMenuActions(MenuItems.RedlineCircle),
					title: _('Circle'),
				},
				{
					key: MenuItems.RedlineArrow,
					icon: <RedlineArrow />,
					onClick: () => handleMenuActions(MenuItems.RedlineArrow),
					title: _('Arrow'),
				},
				{
					key: MenuItems.Delete,
					icon: <Delete />,
					onClick: () => handleMenuActions(MenuItems.Delete),
					title: _('DeleteObject'),
				},
				{
					key: MenuItems.DeleteRedline,
					icon: <DeleteRedline elementStyle={{ height: 18, width: 18 }} />,
					onClick: () => handleMenuActions(MenuItems.DeleteRedline),
					title: _('DeleteRedline'),
				},
				{
					key: MenuItems.MonitorZone,
					icon: <MonitorZone />,
					onClick: () => handleMenuActions(MenuItems.MonitorZone),
					title: _('MonitorZone'),
				},
				{
					key: MenuItems.Pin,
					icon: <Pin />,
					onClick: ({ domEvent }) => handleMenuActions(MenuItems.Pin, domEvent as React.MouseEvent<HTMLElement>),
					title: _('PinDrawing'),
				},
				{
					key: MenuItems.LineWeight,
					icon: <LineWeight />,
					onClick: () => handleMenuActions(MenuItems.LineWeight),
					title: _('LineWeightConfig'),
				},
				{
					key: MenuItems.FillSelectColorConfig,
					icon: (
						<PaintBucket elementStyle={{ height: 17, width: 21, borderBottom: `2px solid ${redlineConfiguration?.fillColor}`, paddingBottom: 1 }} />
					),
					onClick: ({ domEvent }) => handleMenuActions(MenuItems.FillSelectColorConfig, domEvent as React.MouseEvent<HTMLElement>),
					title: _('FillSelectColorConfig'),
				},
				{
					key: MenuItems.LineSelectColorConfig,
					icon: <LineColor elementStyle={{ height: 17, width: 21, borderBottom: `2px solid ${redlineConfiguration?.color}`, paddingBottom: 1 }} />,
					onClick: ({ domEvent }) => handleMenuActions(MenuItems.LineSelectColorConfig, domEvent as React.MouseEvent<HTMLElement>),
					title: _('LineSelectColorConfig'),
				},
			],
		},
		{
			style,
			disabled,
			icon: <Selection elementStyle={{ height: 20 }} />,
			key: MenuItems.Selection,
			popupClassName: 'graphicsMenuBarSubMenu graphicsMenuBarSubMenuSelection',
			popupOffset: [0, 1],
			title: _('Selection'),
			label: _('Selection'),
			children: [
				{
					key: MenuItems.Select,
					icon: <Select elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.Select),
					title: _('Select'),
				},
				{
					key: MenuItems.Move,
					icon: <Move />,
					onClick: () => handleMenuActions(MenuItems.Move),
					title: _('Move'),
				},
				{
					key: MenuItems.Group,
					icon: <Group elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.Group),
					title: _('Group'),
				},
				{
					key: MenuItems.Ungroup,
					icon: <Ungroup elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.Ungroup),
					title: _('Ungroup'),
				},
				{
					key: MenuItems.Rotate,
					icon: <Rotate elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.Rotate),
					title: _('Rotate'),
				},
				{
					key: MenuItems.Resize,
					icon: <Resize elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.Resize),
					title: _('Resize'),
				},
				{
					key: MenuItems.FlipVertical,
					icon: <FlipVertical elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.FlipVertical),
					title: _('FlipVertical'),
				},
				{
					key: MenuItems.FlipHorizontal,
					icon: <FlipHorizontal elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.FlipHorizontal),
					title: _('FlipHorizontal'),
				},
				{
					key: MenuItems.BringToFront,
					icon: <BringToFront elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.BringToFront),
					title: _('BringToFront'),
				},
				{
					key: MenuItems.SendToBack,
					icon: <SendToBack elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.SendToBack),
					title: _('SendToBack'),
				},
				{
					key: MenuItems.BringForward,
					icon: <BringForward elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.BringForward),
					title: _('BringForward'),
				},
				{
					key: MenuItems.SendBackward,
					icon: <SendBackward elementStyle={{ height: 17, width: 21 }} />,
					onClick: () => handleMenuActions(MenuItems.SendBackward),
					title: _('SendBackward'),
				},
			],
		},
		{
			style,
			disabled,
			icon: <Text elementStyle={{ height: 20 }} />,
			key: MenuItems.Text,
			popupClassName: 'graphicsMenuBarSubMenu graphicsMenuBarSubMenuText',
			popupOffset: [0, 1],
			title: _('Text'),
			label: _('Text'),
			children: [
				{
					key: MenuItems.AddText,
					icon: <AddText />,
					onClick: () => handleMenuActions(MenuItems.AddText),
					title: _('AddText'),
				},
				{
					key: MenuItems.AddParagraph,
					icon: <AddParagraph />,
					onClick: () => handleMenuActions(MenuItems.AddParagraph),
					title: _('AddParagraph'),
				},
				{
					key: MenuItems.SearchText,
					icon: <SearchText />,
					onClick: () => handleMenuActions(MenuItems.SearchText),
					title: _('SearchText'),
				},
			],
		},
	];

	return (
		<div id='mainMenuContainer' className={styles.mainMenuContainer}>
			<Menu
				className={styles.menu}
				activeKey=''
				getPopupContainer={() => document.getElementById('mainMenuContainer')}
				items={items}
				mode='horizontal'
				triggerSubMenuAction='click'
				selectedKeys={[]}
				disabledOverflow={true}
			/>
		</div>
	);
});

export { MainMenu };
